import { defineStore } from 'pinia';
import { api, setLoggingOut } from '../api/client';

export type KycStatus = 'NONE' | 'PENDING' | 'APPROVED';

export interface AuthUser {
  id: string;
  email: string;
  username: string | null;
  role: 'USER' | 'ADMIN' | 'KYC_ADMIN';
  emailVerified: boolean;
  banned: boolean;
  firstName: string | null;
  lastName: string | null;
  country: string | null;
  addressLine1: string | null;
  city: string | null;
  postalCode: string | null;
  phone: string | null;
  birthDate: string | null;
  kycStatus: KycStatus;
  kycLastChangedAt: string | null;
  /** Global flag (admin-toggleable). When false, KYC UI/gating is hidden. */
  kycEnabled: boolean;
  walletAvailableCents: number;
  walletHeldCents: number;
  createdAt: string;
}

interface State {
  user: AuthUser | null;
  loading: boolean;
  initialized: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    user: null,
    loading: false,
    initialized: false,
  }),
  getters: {
    isAuthenticated: (s) => s.user !== null,
    isAdmin: (s) => s.user?.role === 'ADMIN',
    /** Anyone with an /admin entry point: ADMIN, or KYC_ADMIN (KYC queue only). */
    isStaff: (s) => s.user?.role === 'ADMIN' || s.user?.role === 'KYC_ADMIN',
    // KYC is OFF by default — admin must explicitly enable it from the admin
    // Settings page. We mirror the backend default here so KYC UI doesn't
    // briefly flash for unauthenticated / still-loading users.
    kycEnabled: (s) => s.user?.kycEnabled === true,
  },
  actions: {
    async fetchUser() {
      this.loading = true;
      try {
        const { data } = await api.get<AuthUser>('/users/me');
        this.user = data;
      } catch {
        this.user = null;
      } finally {
        this.loading = false;
        this.initialized = true;
      }
    },
    async login(email: string, password: string, altcha?: string) {
      // The cart/favorites swap is handled by the App.vue watcher on
      // `auth.user?.id` — see stores/cart.ts:setOwner.
      await api.post('/auth/login', {
        email,
        password,
        altcha: altcha ?? (import.meta.env.DEV ? 'dev-bypass' : ''),
      });
      await this.fetchUser();
    },
    async register(email: string, password: string, username?: string, altcha?: string) {
      await api.post('/auth/register', {
        email,
        password,
        username,
        altcha: altcha ?? (import.meta.env.DEV ? 'dev-bypass' : ''),
      });
      await this.fetchUser();
    },
    async logout() {
      // Clear the local view of "who's logged in" first so any UI gates
      // (header avatar, cart owner watcher, etc.) flip immediately even if
      // the network call hangs. The axios refresh interceptor is also
      // muted via setLoggingOut(true) so a stray in-flight request that
      // 401s mid-logout can't silently refresh the session back. Backend
      // logout is now @Public(), so it succeeds whether or not the access
      // token has expired — the previous "sign out twice to actually sign
      // out" race came from JwtGuard 401ing this endpoint.
      this.user = null;
      setLoggingOut(true);
      try {
        await api.post('/auth/logout');
      } catch {
        // Backend ack is best-effort. Local state and the refresh-mute
        // above keep us logged out from the SPA's perspective until the
        // cookies expire on their own.
      } finally {
        setLoggingOut(false);
      }
    },
  },
});
